import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Lock, ShoppingBag } from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { AdminModuleAccessSection } from "@/components/admin/AdminModuleAccessSection";
import { AdminPaymentHistorySection } from "@/components/admin/AdminPaymentHistorySection";
import { ModuleThumbnail } from "@/components/modules/ModuleThumbnail";
import { ModulePurchaseDialog } from "@/components/modules/ModulePurchaseDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ActionConfirmDialog } from "@/components/admin/ActionConfirmDialog";
import { getOrderedModuleCatalog, MODULE_LEVEL_TONE } from "@/lib/moduleDisplayInfo";
import { PasswordField } from "@/components/auth/PasswordField";
import { Input } from "@/components/ui/input";
import { ProtectedDeleteDialog } from "@/components/admin/ProtectedDeleteDialog";
import { formatAdminLabel } from "@/lib/adminFormatters";
import { formatCurrency } from "@/lib/currency";
import { hasAdminPermission } from "@/lib/adminPermissions";
import { formatDisplayName, formatDisplayNameString } from "@/lib/nameFormatters";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, createApplicationFeeYocoCheckout, getErrorMessage } from "@/lib/api";


const statusTone = {
  awaiting_eft: "bg-amber-50 text-amber-700",
  paid_confirmed: "bg-emerald-50 text-emerald-700",
  payment_under_review: "bg-orange-50 text-orange-700",
  pending_review: "bg-stone-100 text-stone-700",
  approved: "bg-emerald-50 text-emerald-700",
  awaiting_payment: "bg-amber-50 text-amber-700",
  paid_awaiting_approval: "bg-blue-50 text-blue-700",
  rejected: "bg-rose-50 text-rose-700",
  archived: "bg-slate-200 text-slate-700",
};


const DataCard = ({ title, value, testId, onClick, active = false }) => (
  <Card className={`border-stone-200/80 bg-white/90 shadow-none transition-colors ${active ? "ring-2 ring-[#d9b067]" : "hover:border-[#d9b067]"}`} data-testid={`${testId}-card`}>
    <CardContent className="space-y-3 p-6">
      <button type="button" className="w-full text-left" onClick={onClick} data-testid={`${testId}-button`}>
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500" data-testid={`${testId}-label`}>{title}</p>
        <p className={`mt-3 text-slate-950 ${String(value).length > 16 ? "break-words text-[1.08rem] font-semibold leading-6 tracking-[-0.015em]" : "text-[2rem] font-semibold leading-none tracking-[-0.025em]"}`} data-testid={`${testId}-value`}>{value}</p>
      </button>
    </CardContent>
  </Card>
);


const StatusPill = ({ value, testId }) => (
  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusTone[value] || "bg-slate-100 text-slate-700"}`} data-testid={testId}>
    {formatAdminLabel(value)}
  </span>
);


const studentStatusNotes = {
  application: {
    awaiting_payment: "Your application is saved and waiting for the application fee step.",
    paid_awaiting_approval: "Your payment is confirmed and your application is in review.",
    approved: "Your application has been approved and your portal access is active for programme information.",
    rejected: "A final admissions decision has been recorded for this application. Contact support for guidance on next steps.",
    archived: "Your application record is archived and has been taken out of the active operational queue.",
  },
  payment: {
    awaiting_eft: "Use your student number as the EFT reference and send proof of payment after transfer.",
    paid_confirmed: "The application fee has been confirmed by the admin team.",
    payment_under_review: "Your payment confirmation is being reviewed again by the admin team. Admissions actions are paused until it is resolved.",
  },
  approval: {
    pending_review: "Admin review is still in progress. No module content is unlocked yet.",
    approved: "Approval is complete. You can continue using the portal for programme information and updates.",
    rejected: "The admissions team has completed its review for this application.",
    archived: "This record is archived. Contact support if it needs to be restored.",
  },
};


const getStudentNextStep = (dashboard) => {
  if (dashboard?.approval_status === "approved") {
    return {
      title: "Your application is approved",
      body: "You can now keep using the portal to review your Theology SLP information, module pathway, and payment record.",
    };
  }

  if (dashboard?.approval_status === "rejected") {
    return {
      title: "Your application has been reviewed",
      body: "A final admissions decision is on file. Please contact support if you need guidance on the next step.",
    };
  }

  if (dashboard?.overall_status === "archived") {
    return {
      title: "Your application record is archived",
      body: "This record is currently archived and no longer in the active processing queue.",
    };
  }

  if (dashboard?.payment_status === "payment_under_review") {
    return {
      title: "Your payment is under review",
      body: "The admin team is rechecking your payment confirmation before admissions review can continue.",
    };
  }

  if (dashboard?.payment_status !== "paid_confirmed") {
    return {
      title: "Your next step is the application fee",
      body: "Follow the EFT guidance below, use your student number as the payment reference, and send proof of payment once the transfer is complete.",
    };
  }

  return {
    title: "Your application is awaiting approval",
    body: "Your payment has been confirmed. The admin team will now review your application and update your portal status.",
  };
};


const DetailField = ({ label, value, testId }) => (
  <div className="min-w-0 rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">{label}</p>
    <p className="mt-2 min-w-0 break-words whitespace-pre-wrap text-sm leading-7 text-slate-800">{value}</p>
  </div>
);


const DEFAULT_ADMIN_FILTERS = { search: "", payment_status: "", approval_status: "" };
const DEFAULT_ADMIN_SORT = { column: "created_at", direction: "desc" };
const VALID_ADMIN_CARD_FILTERS = new Set(["all", "pending", "paid_awaiting_approval", "approved", "setup_completed", "archived", "module_m1", "module_m2", "module_m3", "module_m4", "module_m5"]);
const VALID_ADMIN_SORT_COLUMNS = new Set(["name", "email", "student_number", "created_at", "payment_status", "approval_status"]);
const MODULE_ACCESS_CARD_CONFIG = [
  { key: "module_m1", code: "M1", title: "Module 1 Access", testId: "admin-stat-module-1-access" },
  { key: "module_m2", code: "M2", title: "Module 2 Access", testId: "admin-stat-module-2-access" },
  { key: "module_m3", code: "M3", title: "Module 3 Access", testId: "admin-stat-module-3-access" },
  { key: "module_m4", code: "M4", title: "Module 4 Access", testId: "admin-stat-module-4-access" },
  { key: "module_m5", code: "M5", title: "Module 5 Access", testId: "admin-stat-module-5-access" },
];


const createDashboardStateSnapshot = (filters, activeCardFilter, sortConfig) => ({
  filters: { ...filters },
  activeCardFilter,
  sortConfig: { ...sortConfig },
});


const sanitizeDashboardState = (dashboardState) => {
  const rawFilters = dashboardState?.filters || {};
  const rawSortConfig = dashboardState?.sortConfig || {};
  return {
    filters: {
      search: typeof rawFilters.search === "string" ? rawFilters.search : DEFAULT_ADMIN_FILTERS.search,
      payment_status: typeof rawFilters.payment_status === "string" ? rawFilters.payment_status : DEFAULT_ADMIN_FILTERS.payment_status,
      approval_status: typeof rawFilters.approval_status === "string" ? rawFilters.approval_status : DEFAULT_ADMIN_FILTERS.approval_status,
    },
    activeCardFilter: VALID_ADMIN_CARD_FILTERS.has(dashboardState?.activeCardFilter) ? dashboardState.activeCardFilter : "all",
    sortConfig: {
      column: VALID_ADMIN_SORT_COLUMNS.has(rawSortConfig.column) ? rawSortConfig.column : DEFAULT_ADMIN_SORT.column,
      direction: rawSortConfig.direction === "asc" ? "asc" : DEFAULT_ADMIN_SORT.direction,
    },
  };
};


const buildDashboardRequestParams = (filters, activeCardFilter) => {
  const params = {
    search: filters.search.trim(),
    payment_status: filters.payment_status,
    approval_status: filters.approval_status,
    setup_completed: "",
    overall_status: "",
    module_access: "",
  };

  switch (activeCardFilter) {
    case "pending":
      params.payment_status = "";
      params.approval_status = "pending_review";
      break;
    case "paid_awaiting_approval":
      params.payment_status = "paid_confirmed";
      params.approval_status = "pending_review";
      break;
    case "approved":
      params.payment_status = "";
      params.approval_status = "approved";
      break;
    case "setup_completed":
      params.setup_completed = "completed";
      break;
    case "archived":
      params.overall_status = "archived";
      break;
    case "module_m1":
      params.module_access = "M1";
      break;
    case "module_m2":
      params.module_access = "M2";
      break;
    case "module_m3":
      params.module_access = "M3";
      break;
    case "module_m4":
      params.module_access = "M4";
      break;
    case "module_m5":
      params.module_access = "M5";
      break;
    default:
      break;
  }

  return params;
};


export const StudentPortalPage = ({ session }) => {
  const [dashboard, setDashboard] = useState(null);
  const [state, setState] = useState({ loading: true, error: "" });
  const [selectedModuleCode, setSelectedModuleCode] = useState("");
  const [cardCheckoutState, setCardCheckoutState] = useState({ loading: false, error: "" });
  const studentNextStep = getStudentNextStep(dashboard);
  const modulePurchaseReady = dashboard?.application_status === "approved" && dashboard?.payment_status === "paid_confirmed" && dashboard?.approval_status === "approved";
  const applicationFeeCardCheckoutAvailable = dashboard?.payment_status === "awaiting_eft" && dashboard?.yoco?.application_fee_checkout_available;
  const modules = useMemo(() => getOrderedModuleCatalog(dashboard?.modules || []), [dashboard?.modules]);
  const selectedModule = modules.find((module) => module.code === selectedModuleCode) || null;
  const formattedDashboardName = formatDisplayNameString(dashboard?.display_name || "");
  const formattedSessionName = formatDisplayNameString(session?.display_name || "");
  const proofOfPaymentEmail = dashboard?.banking_details?.proof_of_payment_email || "payments@zallywoodunisa.org";

  useEffect(() => {
    let isActive = true;

    const loadDashboard = async () => {
      setState({ loading: true, error: "" });
      try {
        const response = await api.get("/student/dashboard");
        if (!isActive) return;
        setDashboard(response.data);
        setState({ loading: false, error: "" });
      } catch (error) {
        if (!isActive) return;
        setState({ loading: false, error: getErrorMessage(error, "We could not load the student portal.") });
      }
    };

    loadDashboard();
    return () => {
      isActive = false;
    };
  }, []);

  const handleApplicationFeeCardCheckout = async () => {
    setCardCheckoutState({ loading: true, error: "" });
    try {
      const response = await createApplicationFeeYocoCheckout();
      const redirectUrl = response?.data?.redirect_url;
      if (!redirectUrl) {
        setCardCheckoutState({ loading: false, error: "We could not start the Yoco checkout right now." });
        return;
      }
      window.location.assign(redirectUrl);
    } catch (error) {
      setCardCheckoutState({ loading: false, error: getErrorMessage(error, "We could not start the Yoco checkout right now.") });
    }
  };

  if (state.loading) {
    return <p className="text-sm text-slate-600" data-testid="student-portal-loading-text">Loading your student portal...</p>;
  }

  if (state.error) {
    return <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="student-portal-error-message">{state.error}</p>;
  }

  return (
    <div className="space-y-8">
      <Card className="border-stone-200/80 bg-white/92 shadow-[0_18px_40px_rgba(31,45,61,0.06)]" data-testid="student-portal-overview-card">
        <CardContent className="grid gap-8 p-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
          <div className="space-y-4">
            <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid="student-portal-overview-eyebrow">Student portal</p>
            <h2 className="text-[2.45rem] font-semibold leading-[1.08] tracking-[-0.025em] text-slate-950" data-testid="student-portal-overview-title">Welcome back, {formattedDashboardName}</h2>
            <p className="text-[17px] leading-8 text-slate-600" data-testid="student-portal-overview-body">{studentNextStep.title}. {studentNextStep.body}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <DataCard title="Student number" value={dashboard?.student_number} testId="student-portal-student-number" />
            <DataCard title="Signed in as" value={formattedSessionName} testId="student-portal-signed-in-name" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="student-portal-status-card">
        <CardHeader>
          <CardTitle className="text-[2rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="student-portal-status-title">Application progress</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-[1.25rem] border border-stone-200 bg-[#fcfaf5] p-5" data-testid="student-portal-application-status-block">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Application status</p>
            <div className="mt-3"><StatusPill value={dashboard?.application_status} testId="student-portal-application-status-pill" /></div>
            <p className="mt-4 text-sm leading-7 text-slate-600">{studentStatusNotes.application[dashboard?.application_status]}</p>
          </div>
          <div className="rounded-[1.25rem] border border-stone-200 bg-[#fcfaf5] p-5" data-testid="student-portal-payment-status-block">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Payment status</p>
            <div className="mt-3"><StatusPill value={dashboard?.payment_status} testId="student-portal-payment-status-pill" /></div>
            <p className="mt-4 text-sm leading-7 text-slate-600">{studentStatusNotes.payment[dashboard?.payment_status]}</p>
          </div>
          <div className="rounded-[1.25rem] border border-stone-200 bg-[#fcfaf5] p-5" data-testid="student-portal-approval-status-block">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Approval status</p>
            <div className="mt-3"><StatusPill value={dashboard?.approval_status} testId="student-portal-approval-status-pill" /></div>
            <p className="mt-4 text-sm leading-7 text-slate-600">{studentStatusNotes.approval[dashboard?.approval_status]}</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[1fr_0.9fr]">
        <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="student-portal-payment-card">
          <CardHeader>
            <CardTitle className="text-[2rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="student-portal-payment-title">What To Do Next</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="rounded-[1.35rem] border border-stone-200 bg-[#fcfaf5] p-5 text-sm leading-7 text-slate-700" data-testid="student-portal-payment-instructions-card">
              {modulePurchaseReady ? (
                <div className="space-y-4" data-testid="student-portal-approved-next-steps-card">
                  <p className="font-semibold text-slate-900" data-testid="student-portal-payment-next-steps-heading">Your next steps</p>
                  <div className="space-y-3" data-testid="student-portal-payment-steps-card">
                    <p>Congratulations, your application has been approved and your portal access is active.</p>
                    <p>You can now begin your SLP journey by purchasing the modules below. Module purchases are currently handled by EFT. After making payment for a module, please email your proof of payment to <a href={`mailto:${proofOfPaymentEmail}`} className="font-semibold text-slate-950 underline underline-offset-2">{proofOfPaymentEmail}</a> so the admin team can review and grant access.</p>
                    <p>Each module purchase covers the registration fee for that specific module together with access to the module learning material. Once you have completed the required learning and meet the sequence requirements, you will later be able to complete the final assessment for that module.</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4" data-testid="student-portal-unpaid-next-steps-card">
                  <p className="font-semibold text-slate-900" data-testid="student-portal-payment-next-steps-heading">Your next steps</p>
                  <div className="space-y-1.5" data-testid="student-portal-payment-steps-card">
                    <p>1. Pay the non-refundable {formatCurrency(dashboard?.application_fee)} application fee by EFT.</p>
                    <p>2. Use your student number as the payment reference.</p>
                    <p>3. Email proof of payment to <a href={`mailto:${proofOfPaymentEmail}`} className="font-semibold text-slate-950 underline underline-offset-2">{proofOfPaymentEmail}</a>.</p>
                    <p>4. Wait for the admin team to confirm your payment and update your application status.</p>
                  </div>
                </div>
              )}
              <div className="mt-5 rounded-[1.2rem] border border-white/70 bg-white/70 p-4" data-testid="student-portal-payment-banking-card">
                {!modulePurchaseReady ? <p className="font-semibold text-slate-900" data-testid="student-portal-payment-fee">Application fee: {formatCurrency(dashboard?.application_fee)} (non-refundable)</p> : <p className="font-semibold text-slate-900" data-testid="student-portal-payment-fee">Module EFT banking details</p>}
                <div className="mt-3 space-y-1.5">
                  <p data-testid="student-portal-payment-bank-name">Bank: {dashboard?.banking_details?.bank_name}</p>
                  <p data-testid="student-portal-payment-account-name">Account Name: {dashboard?.banking_details?.account_name}</p>
                  <p data-testid="student-portal-payment-account-number">Account Number: {dashboard?.banking_details?.account_number}</p>
                  <p data-testid="student-portal-payment-branch-code">Branch Code: {dashboard?.banking_details?.branch_code}</p>
                </div>
              </div>
            </div>
            {applicationFeeCardCheckoutAvailable ? (
              <div className="rounded-[1.35rem] border border-stone-200 bg-[#fcfaf5] p-5" data-testid="student-portal-card-payment-card">
                <p className="text-sm font-medium text-slate-900" data-testid="student-portal-card-payment-title">Online card payment</p>
                <p className="mt-2 text-sm text-slate-600" data-testid="student-portal-card-payment-body">This controlled sandbox option is available only for approved application-fee test students. Your EFT instructions remain available above.</p>
                {cardCheckoutState.error ? (
                  <p className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="student-portal-card-payment-error-message">{cardCheckoutState.error}</p>
                ) : null}
                <Button type="button" className="mt-4 rounded-full bg-[#1f2d3d] hover:bg-[#15212d]" onClick={handleApplicationFeeCardCheckout} disabled={cardCheckoutState.loading} data-testid="student-portal-card-payment-start-button">
                  {cardCheckoutState.loading ? "Opening Yoco checkout..." : `Pay ${formatCurrency(dashboard?.application_fee)} by card`}
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="student-portal-access-card">
          <CardHeader>
            <CardTitle className="text-[2rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="student-portal-access-title">Portal access and contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm leading-7 text-slate-700">
            <DetailField label="Student" value={formattedDashboardName} testId="student-portal-access-name" />
            <DetailField label="Registered email" value={dashboard?.email} testId="student-portal-access-email" />
            <DetailField label="Phone on file" value={dashboard?.phone_masked} testId="student-portal-access-phone" />
            <div className="rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid="student-portal-access-note">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">MODULE ACCESS NOTE</p>
              <p className="mt-2 text-sm leading-7 text-slate-700">Once your {formatCurrency(dashboard?.application_fee)} non-refundable application fee has been paid and your application has been approved, you may begin your SLP journey by purchasing the modules below.</p>
              <p className="mt-3 text-sm leading-7 text-slate-700">You may purchase modules in any order. However, final assessments must be completed in sequence from Module 1 through to Module 5. If you purchase a later module before completing the earlier modules, you may still receive access to the learning material, but you will only be eligible to complete the final assessment once the required previous modules have been completed.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="student-portal-modules-card">
        <CardHeader>
          <CardTitle className="text-[2rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="student-portal-modules-title">Module overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm leading-7 text-slate-600" data-testid="student-portal-modules-note">
            {modulePurchaseReady
              ? "Your application has been approved and your portal access is active. You may now purchase the modules below by EFT and email proof of payment for review."
              : "All five theology modules stay visible so you can compare the pathway, current pricing, and duration. Module purchasing opens once your application fee has been confirmed and your application has been approved."}
          </p>
          <div className="space-y-6" data-testid="student-portal-modules-stack">
            {modules.map((module) => {
              const moduleSelected = selectedModuleCode === module.code;
              const cardClasses = `border-stone-200/80 bg-white/90 shadow-none transition-all ${module.access_status === "purchase_ready" ? "hover:border-[#f3d5a3]" : ""} ${moduleSelected ? "ring-2 ring-[#d9b067]" : ""}`;

              if (module.access_status === "purchase_ready") {
                return (
                  <Card key={module.code} className={cardClasses} data-testid={`student-portal-module-card-${module.code.toLowerCase()}`}>
                    <button
                      type="button"
                      className="w-full text-left"
                      onClick={() => setSelectedModuleCode(module.code)}
                      data-testid={`student-portal-module-card-button-${module.code.toLowerCase()}`}
                    >
                      <CardContent className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[15rem_1fr] lg:gap-8 lg:p-7">
                        <div className="mx-auto w-full max-w-[16rem] lg:mx-0 lg:max-w-none">
                          <ModuleThumbnail module={module} testIdPrefix={`student-portal-module-${module.code.toLowerCase()}`} />
                        </div>

                        <div className="flex min-w-0 flex-col justify-between gap-6">
                          <div className="space-y-5">
                            <div className="flex flex-wrap items-center gap-3">
                              <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid={`student-portal-module-code-${module.code.toLowerCase()}`}>
                                {module.code.replace("M", "Module ")}
                              </p>
                              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${MODULE_LEVEL_TONE[module.level] || "bg-slate-100 text-slate-700"}`} data-testid={`student-portal-module-level-${module.code.toLowerCase()}`}>
                                {module.level}
                              </span>
                              <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700" data-testid={`student-portal-module-state-${module.code.toLowerCase()}`}>
                                Access purchase-ready
                              </span>
                            </div>

                            <div className="space-y-3">
                              <h3 className="text-[1.8rem] font-semibold leading-tight tracking-[-0.025em] text-slate-950" data-testid={`student-portal-module-title-${module.code.toLowerCase()}`}>
                                {module.title}
                              </h3>
                              <p className="max-w-3xl text-sm leading-7 text-slate-600 sm:text-base" data-testid={`student-portal-module-description-${module.code.toLowerCase()}`}>
                                {module.description}
                              </p>
                            </div>

                            <div className="rounded-[1.35rem] border border-stone-200 bg-[#fcfaf5] p-5" data-testid={`student-portal-module-explore-${module.code.toLowerCase()}`}>
                              <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }}>
                                What you will explore
                              </p>
                              <ul className="mt-3 space-y-2.5 text-sm leading-7 text-slate-700">
                                {module.explore.map((item, index) => (
                                  <li key={item} className="flex gap-3" data-testid={`student-portal-module-explore-item-${module.code.toLowerCase()}-${index + 1}`}>
                                    <span className="mt-2 h-2 w-2 rounded-full" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)" }} />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          <div className="flex flex-col gap-4 border-t border-stone-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Current price</p>
                              <p className="mt-1 text-2xl font-semibold tracking-[-0.02em] text-slate-950" data-testid={`student-portal-module-price-${module.code.toLowerCase()}`}>{formatCurrency(module.price)}</p>
                              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500" data-testid={`student-portal-module-duration-${module.code.toLowerCase()}`}>Duration: {module.duration}</p>
                            </div>
                            <div className="flex items-center gap-3 rounded-full px-5 py-3 text-sm font-semibold" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} data-testid={`student-portal-module-cta-${module.code.toLowerCase()}`}>
                              <ShoppingBag className="h-4 w-4" />
                              <span>Buy module</span>
                              <ArrowRight className="h-4 w-4" />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </button>
                  </Card>
                );
              }

              if (module.access_status === "access_granted") {
                return (
                  <Card key={module.code} className={cardClasses} data-testid={`student-portal-module-card-${module.code.toLowerCase()}`}>
                    <CardContent className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[15rem_1fr] lg:gap-8 lg:p-7">
                      <div className="mx-auto w-full max-w-[16rem] lg:mx-0 lg:max-w-none">
                        <ModuleThumbnail module={module} testIdPrefix={`student-portal-module-${module.code.toLowerCase()}`} />
                      </div>

                      <div className="flex min-w-0 flex-col justify-between gap-6">
                        <div className="space-y-5">
                          <div className="flex flex-wrap items-center gap-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid={`student-portal-module-code-${module.code.toLowerCase()}`}>
                              {module.code.replace("M", "Module ")}
                            </p>
                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${MODULE_LEVEL_TONE[module.level] || "bg-slate-100 text-slate-700"}`} data-testid={`student-portal-module-level-${module.code.toLowerCase()}`}>
                              {module.level}
                            </span>
                            <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700" data-testid={`student-portal-module-state-${module.code.toLowerCase()}`}>
                              Access granted
                            </span>
                          </div>

                          <div className="space-y-3">
                            <h3 className="text-[1.8rem] font-semibold leading-tight tracking-[-0.025em] text-slate-950" data-testid={`student-portal-module-title-${module.code.toLowerCase()}`}>
                              {module.title}
                            </h3>
                            <p className="max-w-3xl text-sm leading-7 text-slate-600 sm:text-base" data-testid={`student-portal-module-description-${module.code.toLowerCase()}`}>
                              {module.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-col gap-4 border-t border-stone-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Current price</p>
                            <p className="mt-1 text-2xl font-semibold tracking-[-0.02em] text-slate-950" data-testid={`student-portal-module-price-${module.code.toLowerCase()}`}>{formatCurrency(module.price)}</p>
                            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500" data-testid={`student-portal-module-duration-${module.code.toLowerCase()}`}>Duration: {module.duration}</p>
                          </div>
                          <div className="space-y-2 text-left">
                            {module.reader_supported ? (
                              <Button asChild className="rounded-full bg-[#1f2d3d] hover:bg-[#15212d]" data-testid={`student-portal-module-read-button-${module.code.toLowerCase()}`}>
                                <Link to={module.reader_route || `/portal/modules/${module.code}/read`}>
                                  <span>Read module</span>
                                  <ArrowRight className="h-4 w-4" />
                                </Link>
                              </Button>
                            ) : (
                              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700" data-testid={`student-portal-module-cta-${module.code.toLowerCase()}`}>
                                <ShoppingBag className="h-4 w-4" />
                                <span>Access granted</span>
                              </div>
                            )}
                            <p className="text-sm text-slate-600" data-testid={`student-portal-module-access-note-${module.code.toLowerCase()}`}>
                              {module.reading_message}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }

              if (module.access_status === "access_under_review") {
                return (
                  <Card key={module.code} className={cardClasses} data-testid={`student-portal-module-card-${module.code.toLowerCase()}`}>
                    <CardContent className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[15rem_1fr] lg:gap-8 lg:p-7">
                      <div className="mx-auto w-full max-w-[16rem] lg:mx-0 lg:max-w-none">
                        <ModuleThumbnail module={module} testIdPrefix={`student-portal-module-${module.code.toLowerCase()}`} />
                      </div>

                      <div className="flex min-w-0 flex-col justify-between gap-6">
                        <div className="space-y-5">
                          <div className="flex flex-wrap items-center gap-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid={`student-portal-module-code-${module.code.toLowerCase()}`}>
                              {module.code.replace("M", "Module ")}
                            </p>
                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${MODULE_LEVEL_TONE[module.level] || "bg-slate-100 text-slate-700"}`} data-testid={`student-portal-module-level-${module.code.toLowerCase()}`}>
                              {module.level}
                            </span>
                            <span className="inline-flex rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700" data-testid={`student-portal-module-state-${module.code.toLowerCase()}`}>
                              Access under review
                            </span>
                          </div>

                          <div className="space-y-3">
                            <h3 className="text-[1.8rem] font-semibold leading-tight tracking-[-0.025em] text-slate-950" data-testid={`student-portal-module-title-${module.code.toLowerCase()}`}>
                              {module.title}
                            </h3>
                            <p className="max-w-3xl text-sm leading-7 text-slate-600 sm:text-base" data-testid={`student-portal-module-description-${module.code.toLowerCase()}`}>
                              {module.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-col gap-4 border-t border-stone-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Current price</p>
                            <p className="mt-1 text-2xl font-semibold tracking-[-0.02em] text-slate-950" data-testid={`student-portal-module-price-${module.code.toLowerCase()}`}>{formatCurrency(module.price)}</p>
                            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500" data-testid={`student-portal-module-duration-${module.code.toLowerCase()}`}>Duration: {module.duration}</p>
                          </div>
                          <div className="space-y-2 text-left">
                            <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700" data-testid={`student-portal-module-cta-${module.code.toLowerCase()}`}>
                              <Lock className="h-4 w-4" />
                              <span>Payment under review</span>
                            </div>
                            <p className="text-sm text-slate-600" data-testid={`student-portal-module-lock-${module.code.toLowerCase()}`}>
                              The admin team is reviewing your EFT proof before access is granted.
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }

              if (module.access_status === "access_revoked") {
                return (
                  <Card key={module.code} className={cardClasses} data-testid={`student-portal-module-card-${module.code.toLowerCase()}`}>
                    <CardContent className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[15rem_1fr] lg:gap-8 lg:p-7">
                      <div className="mx-auto w-full max-w-[16rem] lg:mx-0 lg:max-w-none">
                        <ModuleThumbnail module={module} testIdPrefix={`student-portal-module-${module.code.toLowerCase()}`} />
                      </div>

                      <div className="flex min-w-0 flex-col justify-between gap-6">
                        <div className="space-y-5">
                          <div className="flex flex-wrap items-center gap-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid={`student-portal-module-code-${module.code.toLowerCase()}`}>
                              {module.code.replace("M", "Module ")}
                            </p>
                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${MODULE_LEVEL_TONE[module.level] || "bg-slate-100 text-slate-700"}`} data-testid={`student-portal-module-level-${module.code.toLowerCase()}`}>
                              {module.level}
                            </span>
                            <span className="inline-flex rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700" data-testid={`student-portal-module-state-${module.code.toLowerCase()}`}>
                              Access removed
                            </span>
                          </div>

                          <div className="space-y-3">
                            <h3 className="text-[1.8rem] font-semibold leading-tight tracking-[-0.025em] text-slate-950" data-testid={`student-portal-module-title-${module.code.toLowerCase()}`}>
                              {module.title}
                            </h3>
                            <p className="max-w-3xl text-sm leading-7 text-slate-600 sm:text-base" data-testid={`student-portal-module-description-${module.code.toLowerCase()}`}>
                              {module.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-col gap-4 border-t border-stone-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Current price</p>
                            <p className="mt-1 text-2xl font-semibold tracking-[-0.02em] text-slate-950" data-testid={`student-portal-module-price-${module.code.toLowerCase()}`}>{formatCurrency(module.price)}</p>
                            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500" data-testid={`student-portal-module-duration-${module.code.toLowerCase()}`}>Duration: {module.duration}</p>
                          </div>
                          <div className="space-y-2 text-left">
                            <div className="inline-flex items-center gap-2 rounded-full bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" data-testid={`student-portal-module-cta-${module.code.toLowerCase()}`}>
                              <Lock className="h-4 w-4" />
                              <span>Access removed</span>
                            </div>
                            <p className="text-sm text-slate-600" data-testid={`student-portal-module-revoked-note-${module.code.toLowerCase()}`}>
                              Access to this module has been removed. Please contact support if you need help.
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }

              return (
                <Card key={module.code} className={cardClasses} data-testid={`student-portal-module-card-${module.code.toLowerCase()}`}>
                  <CardContent className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[15rem_1fr] lg:gap-8 lg:p-7">
                    <div className="mx-auto w-full max-w-[16rem] lg:mx-0 lg:max-w-none">
                      <ModuleThumbnail module={module} testIdPrefix={`student-portal-module-${module.code.toLowerCase()}`} />
                    </div>

                    <div className="flex min-w-0 flex-col justify-between gap-6">
                      <div className="space-y-5">
                        <div className="flex flex-wrap items-center gap-3">
                          <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid={`student-portal-module-code-${module.code.toLowerCase()}`}>
                            {module.code.replace("M", "Module ")}
                          </p>
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${MODULE_LEVEL_TONE[module.level] || "bg-slate-100 text-slate-700"}`} data-testid={`student-portal-module-level-${module.code.toLowerCase()}`}>
                            {module.level}
                          </span>
                          <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700" data-testid={`student-portal-module-state-${module.code.toLowerCase()}`}>
                            Locked
                          </span>
                        </div>

                        <div className="space-y-3">
                          <h3 className="text-[1.8rem] font-semibold leading-tight tracking-[-0.025em] text-slate-950" data-testid={`student-portal-module-title-${module.code.toLowerCase()}`}>
                            {module.title}
                          </h3>
                          <p className="max-w-3xl text-sm leading-7 text-slate-600 sm:text-base" data-testid={`student-portal-module-description-${module.code.toLowerCase()}`}>
                            {module.description}
                          </p>
                        </div>

                        <div className="rounded-[1.35rem] border border-stone-200 bg-[#fcfaf5] p-5" data-testid={`student-portal-module-explore-${module.code.toLowerCase()}`}>
                          <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }}>
                            What you will explore
                          </p>
                          <ul className="mt-3 space-y-2.5 text-sm leading-7 text-slate-700">
                            {module.explore.map((item, index) => (
                              <li key={item} className="flex gap-3" data-testid={`student-portal-module-explore-item-${module.code.toLowerCase()}-${index + 1}`}>
                                <span className="mt-2 h-2 w-2 rounded-full" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)" }} />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="flex flex-col gap-4 border-t border-stone-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Current price</p>
                          <p className="mt-1 text-2xl font-semibold tracking-[-0.02em] text-slate-950" data-testid={`student-portal-module-price-${module.code.toLowerCase()}`}>{formatCurrency(module.price)}</p>
                          <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500" data-testid={`student-portal-module-duration-${module.code.toLowerCase()}`}>Duration: {module.duration}</p>
                        </div>
                        <div className="space-y-2 text-left">
                          <div className="inline-flex items-center gap-2 rounded-full bg-slate-200 px-4 py-3 text-sm font-semibold text-slate-700" data-testid={`student-portal-module-cta-${module.code.toLowerCase()}`}>
                            <Lock className="h-4 w-4" />
                            <span>Buy module unavailable</span>
                          </div>
                          <p className="text-sm text-slate-600" data-testid={`student-portal-module-lock-${module.code.toLowerCase()}`}>
                            Pay the application fee and wait for admin approval before module purchasing opens.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <ModulePurchaseDialog module={selectedModule} open={Boolean(selectedModule)} onOpenChange={(nextOpen) => { if (!nextOpen) setSelectedModuleCode(""); }} bankingDetails={dashboard?.banking_details} />
    </div>
  );
};


export const AdminDashboardPage = ({ session, refreshSession }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboard, setDashboard] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_ADMIN_FILTERS);
  const [activeCardFilter, setActiveCardFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState(DEFAULT_ADMIN_SORT);
  const [status, setStatus] = useState({ loading: true, error: "", success: "" });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "" });
  const loadDashboard = async (nextFilters = filters, nextActiveCardFilter = activeCardFilter) => {
    const resolvedFilters = nextFilters && typeof nextFilters === "object" && "search" in nextFilters
      ? nextFilters
      : filters;
    const resolvedCardFilter = typeof nextActiveCardFilter === "string" ? nextActiveCardFilter : activeCardFilter;
    if (session?.must_change_password) {
      setStatus((current) => ({ ...current, loading: false }));
      return;
    }
    setStatus((current) => ({ ...current, loading: true, error: "" }));
    try {
      const response = await api.get("/admin/dashboard", { params: buildDashboardRequestParams(resolvedFilters, resolvedCardFilter) });
      setDashboard(response.data);
      setStatus({ loading: false, error: "", success: "" });
    } catch (error) {
      setStatus({ loading: false, error: getErrorMessage(error, "We could not load the admin dashboard."), success: "" });
    }
  };

  const applyCardFilter = (cardKey) => {
    setActiveCardFilter(cardKey);
    loadDashboard(filters, cardKey);
  };

  const toggleSort = (column) => {
    setSortConfig((current) => ({
      column,
      direction: current.column === column && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedApplicants = (() => {
    const items = [...(dashboard?.applications || [])];
    const directionFactor = sortConfig.direction === "asc" ? 1 : -1;
    return items.sort((left, right) => {
      const valueMap = {
        name: `${left.first_name} ${left.surname}`.localeCompare(`${right.first_name} ${right.surname}`),
        email: left.email.localeCompare(right.email),
        student_number: left.student_number.localeCompare(right.student_number),
        created_at: new Date(left.created_at).getTime() - new Date(right.created_at).getTime(),
        payment_status: formatAdminLabel(left.payment_status).localeCompare(formatAdminLabel(right.payment_status)),
        approval_status: formatAdminLabel(left.approval_status).localeCompare(formatAdminLabel(right.approval_status)),
      };
      return (valueMap[sortConfig.column] || 0) * directionFactor;
    });
  })();

  const sortIndicator = (column) => sortConfig.column === column ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕";

  useEffect(() => {
    let isActive = true;

    const loadInitialDashboard = async () => {
      if (session?.must_change_password) {
        setStatus((current) => ({ ...current, loading: false }));
        return;
      }
      const restoredState = sanitizeDashboardState(location.state?.dashboardState);
      setFilters(restoredState.filters);
      setActiveCardFilter(restoredState.activeCardFilter);
      setSortConfig(restoredState.sortConfig);
      setStatus((current) => ({ ...current, loading: true, error: "" }));
      try {
        const response = await api.get("/admin/dashboard", {
          params: buildDashboardRequestParams(restoredState.filters, restoredState.activeCardFilter),
        });
        if (!isActive) return;
        setDashboard(response.data);
        setStatus({ loading: false, error: "", success: "" });
      } catch (error) {
        if (!isActive) return;
        setStatus({ loading: false, error: getErrorMessage(error, "We could not load the admin dashboard."), success: "" });
      }
    };

    loadInitialDashboard();
    return () => {
      isActive = false;
    };
  }, [location.state, session?.must_change_password]);

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    try {
      await api.post("/auth/admin/change-password", passwordForm);
      await refreshSession();
      setStatus({ loading: false, error: "", success: "Password updated. The dashboard is now unlocked." });
    } catch (error) {
      setStatus({ loading: false, error: getErrorMessage(error, "We could not update the password."), success: "" });
    }
  };

  if (session?.must_change_password) {
    return (
      <Card className="border-stone-200/80 bg-white/90 shadow-none" data-testid="admin-force-password-card">
        <CardHeader>
          <CardTitle className="text-3xl font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="admin-force-password-title">Change the seeded admin password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm leading-7 text-slate-600" data-testid="admin-force-password-description">This is required before any admin dashboard actions become available.</p>
          {status.error && <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-force-password-error-message">{status.error}</p>}
          {status.success && <p className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" data-testid="admin-force-password-success-message">{status.success}</p>}
          <form className="space-y-4 max-w-xl" onSubmit={handlePasswordChange}>
            <PasswordField placeholder="Current password" data-testid="admin-force-password-current-input" value={passwordForm.current_password} onChange={(event) => setPasswordForm((current) => ({ ...current, current_password: event.target.value }))} />
            <PasswordField placeholder="New password" data-testid="admin-force-password-new-input" value={passwordForm.new_password} onChange={(event) => setPasswordForm((current) => ({ ...current, new_password: event.target.value }))} />
            <Button type="submit" className="rounded-full bg-[#1f2d3d] hover:bg-[#15212d]" data-testid="admin-force-password-submit-button">Update password</Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {status.error && <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-dashboard-error-message">{status.error}</p>}
      {status.success && <p className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" data-testid="admin-dashboard-success-message">{status.success}</p>}

      <Card className="border-stone-200/80 bg-white/92 shadow-[0_18px_40px_rgba(31,45,61,0.05)]" data-testid="admin-dashboard-overview-card">
        <CardContent className="flex flex-wrap items-end justify-between gap-4 p-8">
          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid="admin-dashboard-overview-eyebrow">Dashboard overview</p>
            <h2 className="text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.025em] text-slate-950" data-testid="admin-dashboard-overview-title">Applicant monitoring</h2>
            <p className="text-[17px] leading-8 text-slate-600" data-testid="admin-dashboard-overview-description">Review applicants in the admissions pipeline, monitor payment and approval states, and open any record for more detailed action handling.</p>
          </div>
          <p className="text-sm leading-7 text-slate-600" data-testid="admin-dashboard-overview-support-text">Use the filters below to search by student number, name, or email.</p>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        <DataCard title="Total applicants" value={dashboard?.stats?.total_applications || 0} testId="admin-stat-total-applications" onClick={() => applyCardFilter("all")} active={activeCardFilter === "all"} />
        <DataCard title="Pending applicants" value={dashboard?.stats?.pending_applications || 0} testId="admin-stat-pending-applications" onClick={() => applyCardFilter("pending")} active={activeCardFilter === "pending"} />
        <DataCard title="Paid awaiting approval" value={dashboard?.stats?.paid_awaiting_approval || 0} testId="admin-stat-paid-awaiting-approval" onClick={() => applyCardFilter("paid_awaiting_approval")} active={activeCardFilter === "paid_awaiting_approval"} />
        <DataCard title="Approved applicants" value={dashboard?.stats?.approved_applications || 0} testId="admin-stat-approved-applications" onClick={() => applyCardFilter("approved")} active={activeCardFilter === "approved"} />
        <DataCard title="Archived Students" value={dashboard?.stats?.archived_students || 0} testId="admin-stat-archived-students" onClick={() => applyCardFilter("archived")} active={activeCardFilter === "archived"} />
        <DataCard title="Setup completed" value={dashboard?.stats?.setup_completed_applicants || 0} testId="admin-stat-setup-completed" onClick={() => applyCardFilter("setup_completed")} active={activeCardFilter === "setup_completed"} />
      </div>

      <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="admin-dashboard-module-access-section">
        <CardHeader>
          <CardTitle className="text-[1.6rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="admin-dashboard-module-access-title">Module access</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 pt-0 sm:grid-cols-2 xl:grid-cols-5">
          {MODULE_ACCESS_CARD_CONFIG.map((card) => (
            <DataCard
              key={card.key}
              title={card.title}
              value={dashboard?.stats?.module_access_counts?.[card.code] || 0}
              testId={card.testId}
              onClick={() => applyCardFilter(card.key)}
              active={activeCardFilter === card.key}
            />
          ))}
        </CardContent>
      </Card>

      <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="admin-dashboard-filters-card">
        <CardHeader>
          <CardTitle className="text-[1.6rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="admin-dashboard-filters-title">Search and filter applicants</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 p-6 pt-0 lg:grid-cols-[1.3fr_0.85fr_0.85fr_auto]">
          <Input placeholder="Search by student number, name, or email" data-testid="admin-search-input" value={filters.search} onChange={(event) => { setFilters((current) => ({ ...current, search: event.target.value })); }} />
          <select className="flex h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="admin-payment-filter-select" value={filters.payment_status} onChange={(event) => { setFilters((current) => ({ ...current, payment_status: event.target.value })); }}>
            <option value="">All payment states</option>
            <option value="awaiting_eft">Awaiting EFT</option>
            <option value="payment_under_review">Payment under review</option>
            <option value="paid_confirmed">Paid confirmed</option>
          </select>
          <select className="flex h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="admin-approval-filter-select" value={filters.approval_status} onChange={(event) => { setFilters((current) => ({ ...current, approval_status: event.target.value })); }}>
            <option value="">All approval states</option>
            <option value="pending_review">Pending review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <Button type="button" className="rounded-full" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} onClick={() => loadDashboard(filters, activeCardFilter)} data-testid="admin-filter-submit-button">Apply filters</Button>
        </CardContent>
      </Card>

      <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="admin-dashboard-table-card">
        <CardHeader>
          <CardTitle className="text-[1.8rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="admin-dashboard-table-title">Applicants</CardTitle>
        </CardHeader>
        <CardContent>
          {status.loading ? (
            <p className="text-sm text-slate-600" data-testid="admin-dashboard-loading-text">Loading applicants...</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="admin-dashboard-table">
              <TableHeader>
                <TableRow>
                  <TableHead><button type="button" onClick={() => toggleSort("name")} className="font-semibold">Name {sortIndicator("name")}</button></TableHead>
                  <TableHead><button type="button" onClick={() => toggleSort("email")} className="font-semibold">Email {sortIndicator("email")}</button></TableHead>
                  <TableHead><button type="button" onClick={() => toggleSort("student_number")} className="font-semibold">Student number {sortIndicator("student_number")}</button></TableHead>
                  <TableHead>Nationality</TableHead>
                  <TableHead>ID / passport</TableHead>
                  <TableHead><button type="button" onClick={() => toggleSort("payment_status")} className="font-semibold">Payment {sortIndicator("payment_status")}</button></TableHead>
                  <TableHead><button type="button" onClick={() => toggleSort("approval_status")} className="font-semibold">Approval {sortIndicator("approval_status")}</button></TableHead>
                  <TableHead><button type="button" onClick={() => toggleSort("created_at")} className="font-semibold">Created {sortIndicator("created_at")}</button></TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedApplicants.map((item) => (
                  <TableRow key={item.application_id} data-testid={`admin-dashboard-row-${item.application_id}`}>
                    <TableCell data-testid={`admin-dashboard-name-${item.application_id}`}>
                      <div className="space-y-2">
                        <p>{formatDisplayName(item.first_name, item.surname)}</p>
                        {item.overall_status === "archived" ? <span className="inline-flex rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-700" data-testid={`admin-dashboard-overall-status-${item.application_id}`}>Archived</span> : null}
                      </div>
                    </TableCell>
                    <TableCell data-testid={`admin-dashboard-email-${item.application_id}`}>{item.email}</TableCell>
                    <TableCell data-testid={`admin-dashboard-student-number-${item.application_id}`}>{item.student_number}</TableCell>
                    <TableCell data-testid={`admin-dashboard-nationality-${item.application_id}`}>{item.nationality}</TableCell>
                    <TableCell data-testid={`admin-dashboard-identity-${item.application_id}`}>{item.identity_masked}</TableCell>
                    <TableCell><StatusPill value={item.payment_status} testId={`admin-dashboard-payment-${item.application_id}`} /></TableCell>
                    <TableCell><StatusPill value={item.approval_status} testId={`admin-dashboard-approval-${item.application_id}`} /></TableCell>
                    <TableCell data-testid={`admin-dashboard-created-${item.application_id}`}>{new Date(item.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button variant="outline" className="rounded-full border-[#d7c6a9]" onClick={() => navigate(`/admin/applications/${item.application_id}`, { state: { dashboardState: createDashboardStateSnapshot(filters, activeCardFilter, sortConfig) } })} data-testid={`admin-dashboard-view-${item.application_id}`}>
                        View applicant
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


export const AdminApplicationDetailPage = ({ session }) => {
  const { applicationId } = useParams();
  const location = useLocation();
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState({ loading: true, error: "", success: "" });
  const [identity, setIdentity] = useState("");
  const [fullPhone, setFullPhone] = useState("");
  const [fullAddress, setFullAddress] = useState("");
  const dashboardState = location.state?.dashboardState;

  const loadDetail = async () => {
    setStatus((current) => ({ ...current, loading: true, error: "" }));
    try {
      const response = await api.get(`/admin/applications/${applicationId}`);
      setDetail(response.data);
      setStatus({ loading: false, error: "", success: "" });
    } catch (error) {
      setStatus({ loading: false, error: getErrorMessage(error, "We could not load the application detail."), success: "" });
    }
  };

  useEffect(() => {
    let isActive = true;

    const fetchDetail = async () => {
      setStatus((current) => ({ ...current, loading: true, error: "" }));
      try {
        const response = await api.get(`/admin/applications/${applicationId}`);
        if (!isActive) return;
        setDetail(response.data);
        setStatus({ loading: false, error: "", success: "" });
      } catch (error) {
        if (!isActive) return;
        setStatus({ loading: false, error: getErrorMessage(error, "We could not load the application detail."), success: "" });
      }
    };

    fetchDetail();
    return () => {
      isActive = false;
    };
  }, [applicationId]);

  const runAction = async (path, successMessage, method = "post", note = null, surfaceErrors = false) => {
    try {
      const response = method === "post"
        ? note === null
          ? await api.post(path)
          : typeof note === "string"
            ? await api.post(path, { note })
            : await api.post(path, note)
        : await api[method](path);
      const message = `${response.data.message || successMessage}${response.data.email_delivery_status ? ` Email status: ${response.data.email_delivery_status}.` : ""}`;
      await loadDetail();
      setStatus({ loading: false, error: "", success: message });
    } catch (error) {
      const message = getErrorMessage(error, "This admin action could not be completed.");
      setStatus({ loading: false, error: message, success: "" });
      if (surfaceErrors) {
        throw message;
      }
    }
  };

  const handleHardDelete = async (password) => {
    try {
      const response = await api.post(`/admin/applications/${applicationId}/hard-delete`, { password });
      setStatus({ loading: false, error: "", success: response.data.message || "Application deleted successfully." });
      window.location.assign("/admin/dashboard");
    } catch (error) {
      throw getErrorMessage(error, "We could not complete the hard delete.");
    }
  };

  const revealIdentity = async () => {
    try {
      const response = await api.post(`/admin/applications/${applicationId}/reveal-identity`);
      setIdentity(response.data.full_identity);
      setStatus({ loading: false, error: "", success: "Full identity revealed and logged successfully." });
    } catch (error) {
      setStatus({ loading: false, error: getErrorMessage(error, "We could not reveal the identity value."), success: "" });
    }
  };

  const revealPhone = async () => {
    try {
      const response = await api.post(`/admin/applications/${applicationId}/reveal-phone`);
      setFullPhone(response.data.full_phone);
      setStatus({ loading: false, error: "", success: "Full phone number revealed and logged successfully." });
    } catch (error) {
      setStatus({ loading: false, error: getErrorMessage(error, "We could not reveal the phone number."), success: "" });
    }
  };

  const revealAddress = async () => {
    try {
      const response = await api.post(`/admin/applications/${applicationId}/reveal-address`);
      setFullAddress(response.data.full_address);
      setStatus({ loading: false, error: "", success: "Full address revealed and logged successfully." });
    } catch (error) {
      setStatus({ loading: false, error: getErrorMessage(error, "We could not reveal the address."), success: "" });
    }
  };

  if (status.loading) {
    return <p className="text-sm text-slate-600" data-testid="admin-detail-loading-text">Loading application detail...</p>;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid="admin-detail-eyebrow">Application detail</p>
          <h2 className="mt-2 break-words text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.025em] text-slate-950" data-testid="admin-detail-title">{formatDisplayName(detail?.applicant?.first_name, detail?.applicant?.surname)}</h2>
          <p className="mt-2 break-words text-sm text-slate-600" data-testid="admin-detail-student-number">Student number: {detail?.applicant?.student_number}</p>
        </div>
        <Button asChild variant="outline" className="rounded-full border-[#d7c6a9]" data-testid="admin-detail-back-button">
          <Link to="/admin/dashboard" state={dashboardState ? { dashboardState } : undefined}>Back to dashboard</Link>
        </Button>
      </div>

      {status.error && <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-detail-error-message">{status.error}</p>}
      {status.success && <p className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" data-testid="admin-detail-success-message">{status.success}</p>}

      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr] xl:items-start">
        <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="admin-detail-applicant-card">
          <CardHeader>
            <CardTitle className="text-[1.9rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="admin-detail-applicant-title">Applicant summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 text-sm leading-7 text-slate-700">
            <div className="grid gap-4 md:grid-cols-2">
              <DetailField label="Email" value={detail?.applicant?.email} testId="admin-detail-email" />
              <DetailField label="Nationality" value={detail?.applicant?.nationality} testId="admin-detail-nationality" />
              <DetailField label="Country" value={detail?.applicant?.country} testId="admin-detail-country" />
              <DetailField label="ID / passport" value={identity || detail?.applicant?.identity_masked} testId="admin-detail-identity-masked" />
              <DetailField label="Address" value={fullAddress || detail?.applicant?.physical_address_masked} testId="admin-detail-address-masked" />
              <DetailField label="Phone" value={fullPhone || detail?.applicant?.phone_masked} testId="admin-detail-phone-masked" />
              <DetailField label="Setup completed" value={detail?.applicant?.setup_completed_at ? "Yes" : "No"} testId="admin-detail-setup-status" />
            </div>
            <div className="flex flex-wrap gap-3 pt-3">
              {hasAdminPermission(session, "full_access") && <Button variant="outline" className="rounded-full border-[#d7c6a9]" onClick={revealIdentity} data-testid="admin-detail-reveal-identity-button">Reveal full ID / passport</Button>}
              {hasAdminPermission(session, "full_access") && <Button variant="outline" className="rounded-full border-[#d7c6a9]" onClick={revealPhone} data-testid="admin-detail-reveal-phone-button">Reveal full phone number</Button>}
              {hasAdminPermission(session, "full_access") && <Button variant="outline" className="rounded-full border-[#d7c6a9]" onClick={revealAddress} data-testid="admin-detail-reveal-address-button">Reveal full address</Button>}
              {hasAdminPermission(session, "resend_applicant_email") && !detail?.applicant?.setup_completed_at && (
                <Button variant="outline" className="rounded-full border-[#d7c6a9]" onClick={() => runAction(`/admin/applications/${applicationId}/resend-setup-link`, "Setup link resent.")} data-testid="admin-detail-resend-setup-button">Resend setup link</Button>
              )}
              {hasAdminPermission(session, "resend_applicant_email") && <Button variant="outline" className="rounded-full border-[#d7c6a9]" onClick={() => runAction(`/admin/applications/${applicationId}/resend-student-number`, "Student number email resent.")} data-testid="admin-detail-resend-student-number-button">Resend student number email</Button>}
              {hasAdminPermission(session, "resend_applicant_email") && ["approved", "rejected"].includes(detail?.application?.approval_status) && (
                <Button variant="outline" className="rounded-full border-[#d7c6a9]" onClick={() => runAction(`/admin/applications/${applicationId}/resend-approval-email`, "Decision email resent.")} data-testid="admin-detail-resend-approval-button">Resend decision email</Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="admin-detail-status-card">
          <CardHeader>
            <CardTitle className="text-[1.9rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="admin-detail-status-title">Application status and actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid="admin-detail-overall-status-block">
                <p className="text-xs uppercase tracking-[0.24em] text-slate-500">Overall</p>
                <div className="mt-3"><StatusPill value={detail?.application?.overall_status} testId="admin-detail-overall-status-pill" /></div>
              </div>
              <div className="rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid="admin-detail-payment-status-block">
                <p className="text-xs uppercase tracking-[0.24em] text-slate-500">Payment</p>
                <div className="mt-3"><StatusPill value={detail?.application?.payment_status} testId="admin-detail-payment-status-pill" /></div>
              </div>
              <div className="rounded-[1.15rem] border border-stone-200 bg-[#fcfaf5] p-4" data-testid="admin-detail-approval-status-block">
                <p className="text-xs uppercase tracking-[0.24em] text-slate-500">Approval</p>
                <div className="mt-3"><StatusPill value={detail?.application?.approval_status} testId="admin-detail-approval-status-pill" /></div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {hasAdminPermission(session, "delete_application") && detail?.application?.overall_status !== "archived" && (
                <Button variant="outline" className="rounded-full border-[#d7c6a9]" onClick={() => runAction(`/admin/applications/${applicationId}/archive`, "Application archived.")} data-testid="admin-detail-archive-button">Archive record</Button>
              )}
              {hasAdminPermission(session, "delete_application") && detail?.application?.overall_status === "archived" && (
                <Button variant="outline" className="rounded-full border-[#d7c6a9]" onClick={() => runAction(`/admin/applications/${applicationId}/restore`, "Application restored.")} data-testid="admin-detail-restore-button">Restore record</Button>
              )}
              {hasAdminPermission(session, "confirm_payment") && detail?.application?.overall_status !== "archived" && detail?.application?.payment_status !== "paid_confirmed" && (
                <ActionConfirmDialog
                  triggerLabel="Mark fee paid"
                  title="Confirm payment"
                  message="Are you sure you want to mark this payment as confirmed?"
                  onConfirm={(note) => runAction(`/admin/applications/${applicationId}/mark-paid`, "Application fee marked as paid.", "post", note, true)}
                  testIdPrefix="admin-detail-mark-paid"
                  triggerTestId="admin-detail-mark-paid-button"
                  triggerClassName="rounded-full"
                />
              )}
              {hasAdminPermission(session, "full_access") && detail?.application?.overall_status !== "archived" && detail?.application?.payment_status === "paid_confirmed" && (
                <ActionConfirmDialog
                  triggerLabel="Return payment to review"
                  title="Return payment to review"
                  message="Are you sure you want to return this payment to review?"
                  onConfirm={(note) => runAction(`/admin/applications/${applicationId}/return-payment-to-review`, "Payment returned to review.", "post", note, true)}
                  testIdPrefix="admin-detail-return-payment"
                  triggerTestId="admin-detail-return-payment-button"
                  triggerVariant="outline"
                  triggerClassName="rounded-full border-[#d7c6a9]"
                />
              )}
              {hasAdminPermission(session, "approve_application") && detail?.application?.overall_status !== "archived" && detail?.application?.payment_status === "paid_confirmed" && detail?.application?.approval_status === "pending_review" && (
                <ActionConfirmDialog
                  triggerLabel="Approve application"
                  title="Approve application"
                  message="Are you sure you want to approve this application?"
                  onConfirm={(note) => runAction(`/admin/applications/${applicationId}/approve`, "Application approved.", "post", note, true)}
                  testIdPrefix="admin-detail-approve"
                  triggerTestId="admin-detail-approve-button"
                  triggerClassName="rounded-full bg-[#1f2d3d] text-white hover:bg-[#15212d]"
                />
              )}
              {hasAdminPermission(session, "approve_application") && detail?.application?.overall_status !== "archived" && detail?.application?.payment_status === "paid_confirmed" && detail?.application?.approval_status === "pending_review" && (
                <ActionConfirmDialog
                  triggerLabel="Reject application"
                  title="Reject application"
                  message="Are you sure you want to reject this application?"
                  onConfirm={(note) => runAction(`/admin/applications/${applicationId}/reject`, "Application rejected.", "post", note, true)}
                  testIdPrefix="admin-detail-reject"
                  triggerTestId="admin-detail-reject-button"
                  triggerVariant="outline"
                  triggerClassName="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50"
                />
              )}
              {hasAdminPermission(session, "full_access") && detail?.application?.overall_status !== "archived" && detail?.application?.approval_status === "approved" && detail?.application?.payment_status === "paid_confirmed" && (
                <ActionConfirmDialog
                  triggerLabel="Return to review"
                  title="Return this application to review"
                  message="Are you sure you want to return this application to review?"
                  onConfirm={(note) => runAction(`/admin/applications/${applicationId}/return-to-review`, "Application returned to review.", "post", note, true)}
                  testIdPrefix="admin-detail-return-to-review"
                  triggerTestId="admin-detail-return-to-review-button"
                  triggerVariant="outline"
                  triggerClassName="rounded-full border-[#d7c6a9]"
                />
              )}
              {hasAdminPermission(session, "full_access") && detail?.application?.overall_status !== "archived" && detail?.application?.approval_status === "rejected" && detail?.application?.payment_status === "paid_confirmed" && (
                <ActionConfirmDialog
                  triggerLabel="Reopen application"
                  title="Reopen application"
                  message="Are you sure you want to return this application to review?"
                  onConfirm={(note) => runAction(`/admin/applications/${applicationId}/reopen`, "Application reopened for review.", "post", note, true)}
                  testIdPrefix="admin-detail-reopen"
                  triggerTestId="admin-detail-reopen-button"
                  triggerVariant="outline"
                  triggerClassName="rounded-full border-[#d7c6a9]"
                />
              )}
              {hasAdminPermission(session, "delete_application") && (
                <ProtectedDeleteDialog
                  triggerLabel="Hard delete"
                  title="Hard delete application"
                  description={`Remove ${detail?.applicant?.first_name} ${detail?.applicant?.surname} and linked student setup data permanently.`}
                  onConfirm={handleHardDelete}
                  testIdPrefix="application-hard-delete"
                />
              )}
              {detail?.application?.overall_status === "archived" && (
                <p className="text-sm leading-7 text-slate-600" data-testid="admin-detail-archived-note-text">This record is archived. Restore it before payment or admissions actions can continue.</p>
              )}
              {detail?.application?.payment_status === "paid_confirmed" && ["approved", "rejected"].includes(detail?.application?.approval_status) && (
                <p className="text-sm leading-7 text-slate-600" data-testid="admin-detail-no-further-actions-text">This application already has a final admissions decision on file. Standard payment and decision actions are complete unless a super admin needs to reverse them.</p>
              )}
              {!hasAdminPermission(session, "confirm_payment") && !hasAdminPermission(session, "approve_application") && !hasAdminPermission(session, "delete_application") && (
                <p className="text-sm leading-7 text-slate-600" data-testid="admin-detail-restricted-note-text">Your role has view access only. No payment, admissions, or delete actions are enabled for this account.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <AdminModuleAccessSection detail={detail} session={session} onRunAction={runAction} />

        <AdminPaymentHistorySection detail={detail} />

        <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="admin-detail-email-events-card">
          <CardHeader>
            <CardTitle className="text-[1.8rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="admin-detail-email-events-title">Email events</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(detail?.email_events || []).map((event) => (
              <div key={`${event.event_type}-${event.sent_at}`} className="rounded-[1.25rem] border border-stone-200 bg-[#fcfaf5] p-4 text-sm leading-7 text-slate-700" data-testid={`admin-detail-email-event-${event.event_type}-${event.sent_at}`}>
                <p data-testid={`admin-detail-email-event-type-${event.event_type}-${event.sent_at}`}>Event: {formatAdminLabel(event.event_type)}</p>
                <p data-testid={`admin-detail-email-event-status-${event.event_type}-${event.sent_at}`}>Status: {formatAdminLabel(event.delivery_status)}</p>
                <p data-testid={`admin-detail-email-event-sent-at-${event.event_type}-${event.sent_at}`}>Sent: {new Date(event.sent_at).toLocaleString()}</p>
                {event.error_message && <p data-testid={`admin-detail-email-event-error-${event.event_type}-${event.sent_at}`}>Error: {event.error_message}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};